// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaButtonClickEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Button, { ButtonPresentationProps, ButtonVariants } from "@stoneage/MobiusOverrides/Button/Button";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage";
import { xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imagePath = "imagePath",
    title = "title",
    titleStyle = "titleStyle",
    copy = "copy",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    buttonId = "buttonId",
    url = "url",
    openInNewTab = "openInNewTab",
    colorTheme = "colorTheme",
    gaClickEvent = "gaClickEvent",
    gaBtnClickEventName = "gaBtnClickEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imagePath]: string;
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: ButtonVariants;
        [fields.buttonId]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.colorTheme]: "light" | "dark";
        [fields.gaClickEvent]: boolean;
        [fields.gaBtnClickEventName]: string;
    };
}

type Props = OwnProps;

export interface InfoIconStyles {
    container?: InjectableCss;
    leftWrapper?: InjectableCss;
    rightWrapper?: InjectableCss;
    title?: TypographyProps;
    copy?: TypographyProps;
    button?: ButtonPresentationProps;
    buttonContainer?: InjectableCss;
}

export const infoIconStyles: InfoIconStyles = {
    container: {
        css: css`
            display: flex;
        `,
    },
    leftWrapper: {
        css: css`
            margin-right: 40px;
            height: 52px;
            width: 52px;
            min-height: 52px;
            min-width: 52px;

            &&& {
                img {
                    height: 52px;
                    width: 52px;
                    overflow: hidden;
                    object-fit: contain;
                }
            }
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    copy: {
        css: css`
            margin-bottom: 20px;
            line-height: 22px;
            display: block;
        `,
    },
    button: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: fit-content;
            }
        `,
    },
};

const styles = infoIconStyles;

const InfoIcon = ({
    id,
    fields: {
        imagePath,
        title,
        titleStyle,
        copy,
        buttonText,
        buttonStyle,
        buttonId,
        url,
        openInNewTab,
        colorTheme,
        gaClickEvent,
        gaBtnClickEventName,
    },
}: Props) => {
    const linkUrl = useGetLink(url);

    const getTitleColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    const getCopyColor = () => (colorTheme === "light" ? "text.main" : "common.ui5");

    const copyText = parse(copy, parserOptions);

    const { customerNumber, impersonation } = useAppSelector(state => ({
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    }));

    return (
        <StyledWrapper className="info-icon" {...styles.container}>
            {imagePath && (
                <StyledWrapper {...styles.leftWrapper}>
                    <LazyImage src={imagePath} altText={imagePath} height="52" width="52" isCatalogImage={false} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.rightWrapper}>
                {title?.length > 0 && (
                    <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTitleColor()}>
                        {parse(title, parserOptions)}
                    </Typography>
                )}
                {copyText && copy.length > 0 && (
                    <Typography {...styles.copy} color={getCopyColor()}>
                        {copyText}
                    </Typography>
                )}
                <StyledWrapper {...styles.buttonContainer}>
                    <Button
                        variant={buttonStyle}
                        href={linkUrl?.url}
                        target={openInNewTab ? "_blank" : ""}
                        {...styles.button}
                        rel="noreferral noopener"
                        {...(buttonId && { id: `${buttonId}_${id}` })}
                        {...(gaClickEvent &&
                            gaBtnClickEventName && {
                                onClick: () => {
                                    trackGaButtonClickEvent(
                                        gaBtnClickEventName,
                                        buttonText,
                                        impersonation,
                                        customerNumber,
                                    );
                                },
                            })}
                    >
                        {buttonText || translate("View All")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const buttonStyles =
    BLUEPRINT_NAME === "stoneage"
        ? [
              { displayName: "Primary", value: "primary" },
              { displayName: "Secondary", value: "secondary" },
          ]
        : [
              { displayName: "Primary", value: "primary" },
              { displayName: "Secondary", value: "secondary" },
              { displayName: "tertiary", value: "tertiary" },
          ];

const widgetModule: WidgetModule = {
    component: InfoIcon,
    definition: {
        displayName: "Info Icon",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.imagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.buttonStyle,
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: buttonStyles,
            },
            {
                name: fields.buttonId,
                displayName: "Button Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.gaClickEvent,
                displayName: "Track GA4 Click Event",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.gaBtnClickEventName,
                displayName: "Btn Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.gaClickEvent,
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
        ],
    },
};

export default widgetModule;
